import axios from "@/axios";
import auditLogSignalR from "@/services/audit_log_SignalR";

export const namespaced = true;

// AbortController for cancelling pending requests
let currentAbortController = null;

// Timer for delayed loading spinner (must be outside Vuex state)
let loadingTimerId = null;

export const state = {
  auditLog: [],
  isLoading: false,
  loadingText: "",
  totalAuditLogCount : null,
  currentCountSoFar: 0,
  currentOrderId: null,
  currentPage: 1,
  pageSize: 10,
  hasMorePages: true
};

export const getters = {
  getAuditLog: (state) => {
    return state.auditLog;
  },
  getTotalAuditLogCount: (state) => {
    return state.totalAuditLogCount
  },
  getCurrentPage: (state) => {
    return state.currentPage
  },
  getPageSize: (state) => {
    return state.pageSize
  },
  auditLogLoadingText: (state) => {
    return state.loadingText;
  },
  isLoading: (state) => {
    return state.isLoading;
  },
  getCurrentOrderId: (state) => {
    return state.currentOrderId;
  },
  hasMorePages: (state) => {
    return state.hasMorePages;
  },
  getCurrentCountSoFar: (state) => {
    return state.currentCountSoFar;
  }
};

export const mutations = {
  SET_AUDIT_LOG(state, auditLog) {
    // Simple concat - backend handles deduplication
    state.auditLog = state.auditLog.concat(auditLog);
  },
  REPLACE_AUDIT_LOG(state, auditLog) {
    state.auditLog = auditLog;
  },
  PREPEND_AUDIT_LOG(state, auditLogs) {
    // Add new logs to the beginning (SignalR real-time updates)
    state.auditLog = [...auditLogs, ...state.auditLog];
  },
  CLEAR_AUDIT_LOG(state) {
    state.auditLog = [];
    state.loadingText = "";
    state.currentCountSoFar = 0;
    state.currentPage = 1;
    state.hasMorePages = true;
  },
  SET_AUDIT_LOG_LENGTH(state , number){
    state.totalAuditLogCount = number
  },
  SET_CURRENT_PAGE(state, page) {
    state.currentPage = page
  },
  SET_PAGE_SIZE(state, size) {
    state.pageSize = size
  },
  SET_AUDIT_LOG_LOADING_TEXT(state, text) {
    state.loadingText = text;
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_CURRENT_ORDER_ID(state, orderId) {
    state.currentOrderId = orderId;
  },
  // Batch update for Show More - single mutation instead of multiple
  APPEND_PAGE(state, { results, pageNumber, currentCountSoFar, hasMore }) {
    state.auditLog = state.auditLog.concat(results);
    state.currentPage = pageNumber;
    state.currentCountSoFar = currentCountSoFar;
    state.hasMorePages = hasMore;
    state.isLoading = false;
    state.loadingText = "";
  },
  // Batch update for Show Latest / Initial Load - single mutation for speed
  RESET_TO_PAGE(state, { results, pageNumber, currentCountSoFar, hasMore, totalCount }) {
    state.auditLog = results;
    state.currentPage = pageNumber;
    state.currentCountSoFar = currentCountSoFar;
    state.hasMorePages = hasMore;
    if (totalCount > 0) state.totalAuditLogCount = totalCount;
    state.isLoading = false;
    state.loadingText = "";
  },
  SET_CURRENT_COUNT_SO_FAR(state, count) {
    state.currentCountSoFar = count;
  },
};

// Keep track of the callback so we can remove it later
let currentOrderCallback = null;

export const actions = {
  clearData({ commit, state }) {
    // Unsubscribe from current order if subscribed
    if (state.currentOrderId) {
      auditLogSignalR.unsubscribeFromOrder(state.currentOrderId, currentOrderCallback);
      currentOrderCallback = null;
      commit("SET_CURRENT_ORDER_ID", null);
    }
    commit("CLEAR_AUDIT_LOG");
  },
  async fetchAuditLog({ commit }, orderId = 0) {
    if (orderId != 0) {
      await axios
        .get(`/api/orderauditlogs/${orderId}`)
        .then((res) => {
          if (res.data.length > 0) {
            commit("SET_AUDIT_LOG_LOADING_TEXT", "Audit Logs Loaded...");
            commit("SET_AUDIT_LOG", res.data);
          } else {
            commit("SET_AUDIT_LOG_LOADING_TEXT", "No Audit Logs Available!");
          }
        })
        .catch((err) => {
          //console.log(err); //not handelled
        });
    } else {
      commit("CLEAR_AUDIT_LOG");
      //commit("SET_AUDIT_LOG", DummyAuditData);
    }
  },
  async fetchAuditLogPagination({ commit, state, dispatch }, apiInfo) {
    try {
      // Cancel any pending request
      if (currentAbortController) {
        currentAbortController.abort();
      }
      currentAbortController = new AbortController();
      
      // Unsubscribe from previous order if switching orders (non-blocking)
      if (apiInfo.isClear && state.currentOrderId && state.currentOrderId !== apiInfo.orderId) {
        auditLogSignalR.unsubscribeFromOrder(state.currentOrderId, currentOrderCallback);
        currentOrderCallback = null;
      }
      
      // For "Show More" (not clearing), show loading immediately since user clicked a button
      // For initial load or search (clearing), delay showing spinner to avoid flash on fast responses
      if (loadingTimerId) clearTimeout(loadingTimerId);
      if (!apiInfo.isClear) {
        // Show More - immediate feedback
        commit("SET_LOADING", true);
      } else {
        // Initial load or search - delay 300ms
        loadingTimerId = setTimeout(() => {
          commit("SET_LOADING", true);
        }, 300);
      }

      // Build URL - only include search parameter if it has a value
      const searchParam = apiInfo.searchText ? `/${encodeURIComponent(apiInfo.searchText)}` : '';
      let res = await axios.get(
        `/api/orderauditlogs/pagination/${apiInfo.orderId}/${apiInfo.pageNumber}/${apiInfo.resultsPerPage}${searchParam}`,
        { signal: currentAbortController.signal }
      )

      // Clear timer - response arrived before delay
      if (loadingTimerId) {
        clearTimeout(loadingTimerId);
        loadingTimerId = null;
      }

      if (res.data.results.length > 0) {
        // Calculate hasMore
        const hasMore = res.data.results.length >= apiInfo.resultsPerPage;
        const currentCountSoFar = res.data.currentCountSoFar || (apiInfo.pageNumber * apiInfo.resultsPerPage);
        
        if (apiInfo.isClear) {
          // Initial load, search, or Show Latest - single batch mutation for speed
          commit("RESET_TO_PAGE", {
            results: res.data.results,
            pageNumber: apiInfo.pageNumber,
            currentCountSoFar: currentCountSoFar,
            hasMore: hasMore,
            totalCount: res.data.recordCount || 0
          });
        } else {
          // Show More - single batch mutation for speed
          commit("APPEND_PAGE", {
            results: res.data.results,
            pageNumber: apiInfo.pageNumber,
            currentCountSoFar: currentCountSoFar,
            hasMore: hasMore
          });
        }

        // Subscribe to real-time updates for this order (only on first page, non-blocking)
        if (apiInfo.pageNumber === 1 || apiInfo.isClear) {
          const subscribedOrderId = apiInfo.orderId;
          commit("SET_CURRENT_ORDER_ID", subscribedOrderId);
          
          // Create callback with closure over the correct values
          currentOrderCallback = (data) => {
            if (data && data.auditLogs && data.orderId === subscribedOrderId) {
              commit("PREPEND_AUDIT_LOG", data.auditLogs);
              // Update total count and current displayed count
              if (state.totalAuditLogCount != null) {
                commit("SET_AUDIT_LOG_LENGTH", state.totalAuditLogCount + data.auditLogs.length);
              }
              commit("SET_CURRENT_COUNT_SO_FAR", state.currentCountSoFar + data.auditLogs.length);
            }
          };
          
          // Don't await - let SignalR subscribe in background
          auditLogSignalR.subscribeToOrder(subscribedOrderId, currentOrderCallback);
        }
      } else {
        commit("SET_AUDIT_LOG_LOADING_TEXT", "No Audit Logs Available!");
      }
    } catch(err) {
      // Ignore cancelled requests (user started a new search)
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      commit("SET_AUDIT_LOG_LOADING_TEXT", "Error loading audit logs");
    } finally {
      // Clear the loading timer and hide spinner
      if (loadingTimerId) {
        clearTimeout(loadingTimerId);
        loadingTimerId = null;
      }
      commit("SET_LOADING", false);
    }
  },
};
